import { Button, DialogContent, DialogActions, CircularProgress, Typography } from "@mui/material";
import { useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { useStore } from "../../../store/store";
import { useRelatedRecords } from "../../../api/useRelatedRecords";
import { ClientAndRelativesEmailSelectionField } from "../../../components/fieldsInputs/fields/customized/ClientAndRelativesEmailSelectionField";
import { api } from "../../../api/config";
import { MAKE_WEBHOOKS } from "../../../utils/constants";
import { isClientLessThan18YearsOld } from "../../../utils/getClientAge";

type SendAppDownloadEmailDialogProps = {
	context: any;
	onClose: () => void;
};

export const SendAppDownloadEmailDialog = ({ context, onClose }: SendAppDownloadEmailDialogProps) => {
	const { notifications } = useStore();
	const [isSending, setIsSending] = useState(false);

	const treatment = context?.item;
	const client = treatment?.client;
	const responsibleForTreatment = treatment?.relative_resposible_for_treatment?.[0]?.relatives_id;

	const clientData = {
		name: `${client?.user_id?.first_name ?? ""} ${client?.user_id?.last_name ?? ""}`.trim(),
		email: client?.user_id?.email,
	};

	const { data: relatives } = useRelatedRecords("relatives", "*.*", "client_id", client?.id);

	const relativesData = relatives?.items?.map((relative: { Nome: string; email: string }) => ({
		name: relative?.Nome || "",
		email: relative?.email || "",
	})) || [];

	const defaultEmailRecipient = responsibleForTreatment
		? { name: responsibleForTreatment.Nome || "", email: responsibleForTreatment.email || "" }
		: clientData;

	const entityForm = useForm({
		defaultValues: {
			emailData: {
				id: `${defaultEmailRecipient.email}-${defaultEmailRecipient.name}`,
				name: defaultEmailRecipient.name,
				email: defaultEmailRecipient.email,
			},
		},
	});

	const sendAppDownloadEmail = async () => {
		const emailData = entityForm.getValues("emailData");
		if (!emailData?.email) {
			notifications.setNotification({
				isOpen: true,
				message: "Selecione um email para enviar",
				severity: "warning",
			});
			return;
		}

		setIsSending(true);
		const payload = buildAppDownloadEmailPayload(treatment, emailData.email);
		try {
			await api({
				method: "POST",
				url: MAKE_WEBHOOKS.send_app_download_email,
				data: payload,
			});
			notifications.setNotification({
				isOpen: true,
				message: `Email de download da app enviado com sucesso para ${emailData.email}`,
				severity: "success",
			});
			onClose();
		} catch (error: any) {
			notifications.setNotification({
				isOpen: true,
				message: error?.response?.data || "Erro ao enviar email de download da app",
				severity: "error",
			});
		} finally {
			setIsSending(false);
		}
	};

	return (
		<>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				<Typography variant="body2" sx={{ mb: 2 }}>
					Envia o email com o link para download da app
				</Typography>
				<ClientAndRelativesEmailSelectionField
					entityForm={entityForm as unknown as UseFormReturn}
					client={clientData}
					relatives={relativesData}
				/>
			</DialogContent>
			<DialogActions>
				<Button color="secondary" onClick={onClose}>
					Fechar
				</Button>
				<Button onClick={sendAppDownloadEmail} disabled={isSending}>
					{isSending ? <CircularProgress color="inherit" size={24} /> : "Enviar"}
				</Button>
			</DialogActions>
		</>
	);
};

const buildAppDownloadEmailPayload = (treatment: any, recipientEmail: string) => {
	const client = treatment?.client;
	const clientFirstName = client?.user_id?.first_name ?? "";
	const clientLastName = client?.user_id?.last_name ?? "";
	const clientName = `${clientFirstName} ${clientLastName}`.trim();
	const clientEmail = client?.user_id?.email ?? "";
	const clientPhone = client?.user_id?.phone ?? "";
	const gender = client?.gender ?? null;
	const clientGenderPronoun = gender === "female" ? "da" : gender === "male" ? "do" : "do(a)";
	const clientIsAdult = client?.birthday ? !isClientLessThan18YearsOld(client.birthday) : true;
	const emailGreeting = makeEmailGreeting(clientIsAdult, clientName, gender);
	const clinicName = client?.clinic_id?.name ?? "";
	const clinicPhone = client?.clinic_id?.receptionist?.phone ?? "";

	const payload = {
		to: recipientEmail,
		treatmentId: treatment?.id,
		dynamicTemplateData: {
			clientName,
			clientEmail,
			clientPhone,
			clinicName,
			clinicPhone,
			clientIsAdult,
			clientGenderPronoun,
			emailGreeting,
		},
	};
	return payload;
};

const makeEmailGreeting = (isAdult: boolean, clientName: string, clientGender: string | null) => {
	if (isAdult) {
		const greeting = `Olá ${clientName},`;
		return greeting;
	}

	const genderWord = clientGender === "male" ? "pelo" : clientGender === "female" ? "pela" : "pelo(a)";
	const greeting = `Caro(a) responsável ${genderWord} ${clientName},`;
	return greeting;
};
